import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Stack,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Warning,
  Security,
  Info,
  Gavel,
} from '@mui/icons-material';

const DisclaimerPage = () => {
  const theme = useTheme();

  const disclaimerSections = [
    {
      icon: <Warning />,
      title: 'ABSOLUTE DISCLAIMER OF WARRANTIES',
      severity: 'error',
      content: [
        'PWGen-Rust is provided "AS IS" and "AS AVAILABLE" with ALL FAULTS and WITHOUT WARRANTY OF ANY KIND.',
        'TO THE MAXIMUM EXTENT PERMITTED BY LAW, we EXPRESSLY DISCLAIM ALL WARRANTIES, whether express, implied, statutory, or otherwise.',
        'This includes but is NOT LIMITED TO warranties of merchantability, fitness for a particular purpose, non-infringement, accuracy, quiet enjoyment, and title.',
        'We make NO WARRANTY that the software will meet your requirements, be uninterrupted, timely, secure, or error-free.',
        'NO ORAL OR WRITTEN INFORMATION or advice given by us shall create a warranty not expressly stated herein.',
        'You acknowledge that no software is 100% secure and that use of any password management software involves inherent security risks.'
      ]
    },
    {
      icon: <Gavel />,
      title: 'COMPLETE LIMITATION OF LIABILITY',
      severity: 'error',
      content: [
        'TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL Kief Studio LLC, its officers, directors, employees, agents, affiliates, subsidiaries, or any PWGen-Rust contributors BE LIABLE FOR ANY DAMAGES WHATSOEVER.',
        'This limitation applies to ALL TYPES OF DAMAGES including but not limited to: direct, indirect, incidental, special, consequential, punitive, exemplary, reliance, or any other damages.',
        'SPECIFIC EXCLUSIONS include but are not limited to: data loss, data corruption, business interruption, lost profits, lost revenue, loss of use, loss of data, cost of substitute goods or services, or any other commercial damages.',
        'Our TOTAL LIABILITY for all claims shall NOT EXCEED the amount you paid for the software (which may be ZERO for open-source users).',
        'These limitations apply REGARDLESS of the legal theory (contract, tort, negligence, strict liability, or otherwise) and even if we have been advised of the possibility of such damages.',
        'Some jurisdictions do not allow these limitations, but they apply to the fullest extent permitted by applicable law.'
      ]
    },
    {
      icon: <Security />,
      title: 'ASSUMPTION OF RISK AND USER RESPONSIBILITIES',
      severity: 'warning',
      content: [
        'YOU ACKNOWLEDGE AND AGREE that password management involves INHERENT RISKS that you VOLUNTARILY ASSUME.',
        'YOU ARE SOLELY RESPONSIBLE for: evaluating the suitability of the software, maintaining security of your master password, implementing additional security measures, and regular data backups.',
        'YOU UNDERSTAND that cryptographic software may be subject to export controls and YOU ARE RESPONSIBLE for compliance with all applicable laws.',
        'YOU ACKNOWLEDGE that the software creates temporary files, cache data, or system traces and YOU ARE RESPONSIBLE for securing your systems.',
        'YOU AGREE to conduct your own security assessments and risk analyses before using the software in any environment.',
        'NO RELIANCE should be placed solely on PWGen-Rust for critical security needs without independent verification and redundant security measures.'
      ]
    },
    {
      icon: <Info />,
      title: 'PROHIBITED USES AND ADDITIONAL DISCLAIMERS',
      severity: 'warning',
      content: [
        'PWGen-Rust is EXPLICITLY NOT INTENDED and MUST NOT BE USED for: life-critical applications, medical devices, aviation systems, nuclear facilities, military systems, or any application where failure could result in death, personal injury, or severe environmental damage.',
        'USE IN SUCH APPLICATIONS IS STRICTLY PROHIBITED and done entirely at your own risk.',
        'We make NO REPRESENTATIONS about the software\'s compliance with any industry standards, regulations, or certification requirements.',
        'THIRD-PARTY COMPONENTS are governed by their respective licenses and we disclaim all liability for their performance or security.',
        'MODIFIED VERSIONS of the software are not supported and may have different security characteristics - use at your own risk.',
        'We reserve the right to discontinue development or support at any time without notice or liability.'
      ]
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          background: alpha(theme.palette.background.default, 0.5),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                background: 'linear-gradient(45deg, #f57c00, #ff9800)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Disclaimer
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ 
                mb: 4, 
                maxWidth: 800, 
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Important information about the use, limitations, and responsibilities regarding PWGen-Rust
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        {/* Important Notice */}
        <Alert 
          severity="warning" 
          icon={<Warning />}
          sx={{ 
            mb: 6,
            p: 3,
            borderRadius: 3,
            fontSize: '1.1rem',
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Important: Please Read Carefully
          </Typography>
          <Typography variant="body1">
            <strong>BINDING LEGAL AGREEMENT:</strong> This disclaimer constitutes a binding legal agreement between you and Kief Studio LLC. 
            BY DOWNLOADING, INSTALLING, OR USING PWGen-Rust, YOU ACKNOWLEDGE that you have read, understood, and LEGALLY AGREE to be bound by ALL terms herein. 
            <strong>IF YOU DO NOT AGREE with any part of this disclaimer, you MUST immediately discontinue use and delete all copies of the software.</strong>
          </Typography>
        </Alert>

        {/* Disclaimer Sections */}
        <Stack spacing={4} sx={{ mb: 8 }}>
          {disclaimerSections.map((section, index) => (
            <Paper
              key={index}
              elevation={2}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box
                  sx={{
                    mr: 2,
                    p: 1.5,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 48,
                    height: 48,
                    backgroundColor: alpha(
                      section.severity === 'error' ? theme.palette.error.main :
                      section.severity === 'warning' ? theme.palette.warning.main :
                      theme.palette.info.main, 0.1
                    ),
                    color: 
                      section.severity === 'error' ? 'error.main' :
                      section.severity === 'warning' ? 'warning.main' :
                      'info.main',
                  }}
                >
                  {React.cloneElement(section.icon, { sx: { fontSize: 24 } })}
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {section.title}
                </Typography>
              </Box>
              
              <Stack spacing={2}>
                {section.content.map((item, idx) => (
                  <Typography key={idx} variant="body1" sx={{ lineHeight: 1.7 }}>
                    • {item}
                  </Typography>
                ))}
              </Stack>
            </Paper>
          ))}
        </Stack>

        {/* Legal Terms */}
        <Paper
          elevation={1}
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
            Legal and Compliance Information
          </Typography>

          <Stack spacing={4}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                1. Open Source License and Legal Framework
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                PWGen-Rust is released under the Apache License 2.0, which EXPLICITLY DISCLAIMS WARRANTIES and LIMITS LIABILITY. 
                This disclaimer SUPPLEMENTS and REINFORCES the Apache License protections. Where applicable law permits, 
                the MOST RESTRICTIVE terms shall apply to provide MAXIMUM PROTECTION from liability. 
                The full license text is available in the software repository and forms part of this binding agreement.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                2. Third-Party Dependencies and Supply Chain Risks
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                PWGen-Rust incorporates third-party libraries and components, each governed by their respective licenses. 
                WE EXPRESSLY DISCLAIM ALL WARRANTIES AND LIABILITY regarding these third-party components. 
                YOU ACKNOWLEDGE that supply chain attacks, vulnerabilities in dependencies, and third-party component failures 
                are ENTIRELY YOUR RISK. We make NO GUARANTEES about the security, reliability, or continued availability 
                of any third-party components and SHALL NOT BE LIABLE for any damages resulting from their use.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                3. Export and Import Regulations
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                PWGen-Rust uses cryptographic software that may be subject to export controls and import 
                regulations in various jurisdictions. Users are responsible for compliance with applicable 
                export control and import laws in their country of residence and use. We do not warrant 
                compliance with any specific regulatory requirements.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                4. Data Processing, Privacy, and Security Risks
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                While PWGen-Rust is designed to operate locally, WE MAKE NO GUARANTEES about data isolation, 
                memory security, or complete prevention of data leakage. YOU ACKNOWLEDGE that the software may create 
                temporary files, swap memory, cache data, leave traces in system logs, or be subject to memory dumps. 
                WE DISCLAIM ALL LIABILITY for data exposure, privacy breaches, or security failures. 
                YOU ARE SOLELY RESPONSIBLE for securing your systems, understanding data residue risks, 
                and implementing additional security controls as needed.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                5. PROHIBITED HIGH-RISK APPLICATIONS
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                PWGen-Rust is ABSOLUTELY NOT DESIGNED, TESTED, OR INTENDED for use in: life-critical applications, 
                medical devices, aviation systems, nuclear facilities, military systems, financial trading systems, 
                autonomous vehicles, industrial control systems, or ANY APPLICATION where failure could result in 
                death, personal injury, financial loss, or environmental damage. USE IN SUCH APPLICATIONS IS 
                STRICTLY PROHIBITED. Any such use is ENTIRELY AT YOUR OWN RISK and you AGREE TO HOLD US HARMLESS 
                from any and all consequences of such prohibited use.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                6. Professional and Commercial Use
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                While PWGen-Rust may be suitable for professional and commercial use, organizations should 
                conduct their own security assessments and risk analyses. We recommend consulting with 
                cybersecurity professionals before deploying password management solutions in enterprise 
                environments. Compliance with industry standards and regulations is the user's responsibility.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                7. Modifications and Contributions
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                Users who modify PWGen-Rust or contribute to its development do so under the terms of the 
                open source license. Contributors grant rights to their contributions as specified in the 
                license. Modified versions of the software are not supported and may have different security 
                characteristics than the original.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                8. INDEMNIFICATION AND HOLD HARMLESS
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, YOU AGREE TO DEFEND, INDEMNIFY, AND HOLD HARMLESS 
                Kief Studio LLC, its officers, directors, employees, agents, affiliates, and all PWGen-Rust contributors 
                from and against ANY AND ALL claims, damages, losses, costs, and expenses (including reasonable attorney fees) 
                arising from or relating to: (a) your use or misuse of the software, (b) your violation of this disclaimer, 
                (c) your violation of any applicable laws, (d) any data loss or security breach, and (e) any third-party claims.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                9. FORCE MAJEURE AND BUSINESS RISKS
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                WE SHALL NOT BE LIABLE for any failure to perform due to circumstances beyond our reasonable control, 
                including but not limited to: acts of God, natural disasters, war, terrorism, cyber attacks, 
                government actions, changes in law, supply chain disruptions, or discontinuation of development. 
                YOU ACKNOWLEDGE that open source software development may cease at any time without notice or liability.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                10. Governing Law and Dispute Resolution
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                This disclaimer is governed by Massachusetts law, without regard to conflict of law principles. 
                Any disputes shall be resolved exclusively in Massachusetts state or federal courts, and you CONSENT 
                to personal jurisdiction there. If any provision is unenforceable, the remainder shall remain in effect. 
                This disclaimer may only be modified in writing signed by an authorized representative of Kief Studio LLC.
              </Typography>
            </Box>
          </Stack>

          <Alert 
            severity="error" 
            sx={{ 
              mt: 6,
              p: 3,
              borderRadius: 2,
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              <strong>FINAL WARNING:</strong> This disclaimer provides MAXIMUM LIABILITY PROTECTION under applicable law. 
              By using PWGen-Rust, you ACKNOWLEDGE that you have read and understood ALL terms and VOLUNTARILY ASSUME ALL RISKS. 
              Questions about this disclaimer should be directed to legal@kief.studio, but NO ORAL OR WRITTEN COMMUNICATION 
              shall modify these terms or create additional warranties or liabilities.
            </Typography>
          </Alert>

          <Box sx={{ mt: 6, p: 3, backgroundColor: alpha(theme.palette.warning.main, 0.1), borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              <strong>Last Updated:</strong> January 2025 | 
              <strong> Version:</strong> 1.0 | 
              <strong> Effective Date:</strong> January 1, 2025
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default DisclaimerPage;